import {
    Device,
    InputDevice,
    MediaStreamKind,
    Participant,
    ParticipantSlot,
    SdkError,
    SlotType,
    SoundProcessorOptions,
    SoundProcessorType,
} from '@yandex-video-platform/goloom-sdk';

import {
    BACKEND_ERROR_CODES_TO_RETRY,
    CLOSE_ERROR_CODES_TO_RETRY,
    DISABLED_SOUND_PROCESSING_OPTIONS,
    GOLOOM_CLOSE_ERROR_CODES_TO_RETRY,
    goloomSdk,
    SOUND_PROCESSING_OPTIONS,
} from '.';
import { NoiseCancellationType } from '../../../../../common/types/settings';
import isOnPremise from '../../helpers/onpremise/is-onpremise';
import { RetryWithTimeoutError } from '../../helpers/redux-saga';

export const isInputDevice = (device: Device): device is InputDevice => {
    return 'track' in device;
};

const getParticipantTracks = (
    participant: Participant,
    kind: 'audio' | 'video',
    mediaStreamKind: MediaStreamKind
): ParticipantSlot[] => {
    if (!goloomSdk.session) {
        return [];
    }

    return goloomSdk.session.slots.current.filter(
        (slot) =>
            slot.type === SlotType.PARTICIPANT &&
            slot.participant?.userId === participant.userId &&
            slot.track?.kind === kind &&
            slot.mediaStreamKind === mediaStreamKind
    ) as ParticipantSlot[];
};

export const isParticipantSharingScreen = (participant: Participant): boolean => {
    const sharingVideoTrack = getParticipantTracks(participant, 'video', MediaStreamKind.DISPLAY);

    return sharingVideoTrack.some(({ track }) => track?.enabled);
};

export const getAudioParticipantTracks = (participant: Participant) => {
    return getParticipantTracks(participant, 'audio', MediaStreamKind.DEVICES);
};

export const getVideoTracks = (participant: Participant) => {
    return getParticipantTracks(participant, 'video', MediaStreamKind.DEVICES);
};

/**
 * Сопоставляет kind медиа-трека к kind девайса
 *
 * @param track {MediaStreamTrack} - медиа-трек
 */
export const getDeviceKindFromTrack = (track: MediaStreamTrack): InputDevice['kind'] | null => {
    const { kind } = track;
    if (kind === 'audio') {
        return 'audioinput';
    }
    if (kind === 'video') {
        return 'videoinput';
    }
    return null;
};

/**
 * Конструктор девайса из трека
 *
 * @param track - медиа-трек
 */
export const createDeviceFromTrack = (track: MediaStreamTrack): InputDevice | null => {
    const { id, label } = track;
    const constraints = track.getConstraints();
    const kind = getDeviceKindFromTrack(track);
    if (kind) {
        return {
            kind,
            id,
            label,
            track,
            group: track.label,
            constraints,
            activeConstraints: constraints,
        } as InputDevice;
    }
    return null;
};

/**
 * Отдаёт параметры процессинга аудио
 *
 * @param isDisabled
 */
export const getSoundProcessingOptions = (isDisabled?: boolean): SoundProcessorOptions => {
    if (isDisabled) {
        return DISABLED_SOUND_PROCESSING_OPTIONS;
    }
    if (isOnPremise) {
        return {
            ...SOUND_PROCESSING_OPTIONS,
            noiseCancellation: false,
        };
    }
    return SOUND_PROCESSING_OPTIONS;
};

const NOISE_CANCELLATION_BY_TYPE: Record<NoiseCancellationType, SoundProcessorOptions['noiseCancellation']> = {
    [NoiseCancellationType.DISABLED]: false,
    [NoiseCancellationType.AUTO]: { type: SoundProcessorType.RNNOISE },
    [NoiseCancellationType.HIGH]: { type: SoundProcessorType.DEEP_FILTER },
    [NoiseCancellationType.LOW]: { type: SoundProcessorType.RNNOISE },
};

export const getNoiseCancellationValueByType = (
    type: NoiseCancellationType
): SoundProcessorOptions['noiseCancellation'] => {
    return NOISE_CANCELLATION_BY_TYPE[type] ?? true;
};

export function getIsSdkError(error: unknown): error is SdkError {
    return error instanceof SdkError;
}

const ROOT_CAUSE_MAX_DEPTH = 100;

export function findRootCause(error: SdkError): unknown {
    let cause = error;
    let counter = 0;
    while (cause?.cause && counter < ROOT_CAUSE_MAX_DEPTH) {
        counter++;
        cause = cause.cause as SdkError;
    }
    return cause;
}

export function getIsSignalingCloseError(error: unknown): error is CloseEvent {
    return error instanceof CloseEvent;
}

export function checkIfShouldRetrySignalingError(error: unknown): boolean {
    const isSdkError = getIsSdkError(error);
    if (!isSdkError) {
        return false;
    }
    const rootCause = findRootCause(error as SdkError);
    const isSdkSignalingCloseError = getIsSignalingCloseError(rootCause);
    const isCodeToRetry = isSdkSignalingCloseError ? CLOSE_ERROR_CODES_TO_RETRY.includes(rootCause?.code) : false;

    return isSdkSignalingCloseError && isCodeToRetry;
}

export function checkIfShouldRetryGoloomError(error: unknown): boolean {
    const isSdkError = getIsSdkError(error);
    if (!isSdkError) {
        return false;
    }
    const rootCause = findRootCause(error as SdkError);
    const isCodeToRetry = getIsSdkError(rootCause) && GOLOOM_CLOSE_ERROR_CODES_TO_RETRY.has(rootCause?.code);

    return isCodeToRetry;
}

export function checkIfShouldStopJoinAttempts(error: unknown, allowGoloomCodes = false) {
    const isTimeoutError = RetryWithTimeoutError.is(error);
    const isSdkSignalingErrorToRetry = checkIfShouldRetrySignalingError(error);
    const isGoloomErrorToRetry = allowGoloomCodes && checkIfShouldRetryGoloomError(error);

    const isBackendErrorToRetry =
        error instanceof Error && Array.from(BACKEND_ERROR_CODES_TO_RETRY).some((code) => error.message.includes(code));

    const isContinue = isTimeoutError || isSdkSignalingErrorToRetry || isBackendErrorToRetry || isGoloomErrorToRetry;

    return !isContinue;
}
